<template>
  <vf-dialog-layout :class-wrapper="classDialogWrapper">
    <template #title>
      {{ title || $t[titleTranslationKey] }}
    </template>
    <section v-if="!closeOnAdd" data-scroll-el="notificationSection">
      <vf-notification
        v-if="cart.notification.message"
        class="mb-4 mt-3 <md:mb-2 md:mt-2"
        :type="cart.notification.type"
        @close="cart.notification.message = ''"
      >
        {{ cart.notification.message }}
        <vf-link v-if="name === 'shopTheLook'" class="text-base" to="/cart">
          {{ $t.viewCart }}
        </vf-link>
      </vf-notification>
    </section>
    <div
      v-style="brandStyles.wrapper"
      :class="mode === 'inline' ? brandClasses.wrapperInline : brandClasses.wrapperModal"
      @keydown.esc="emit('resolve')"
    >
      <define-badge-template>
        <product-badge
          v-if="hasBadge && !showBadgeAboveGallery && mode === 'modal'"
          v-bind="details?.badge"
          class="<lg:my-1"
        />
      </define-badge-template>
      <define-eyebrow-template>
        <template v-if="details?.eyebrow">
          <div v-if="$feature.showSkeletonsOnQuickshop && pending" class="mb-3 h-2 w-18 skeleton" />
          <p v-else-if="details.eyebrow.label" v-bind="eyebrowStyle" class="mb-1 mt-2 truncate eyebrow">
            {{ details.eyebrow.label }}
          </p>
        </template>
      </define-eyebrow-template>

      <define-product-heading>
        <!-- Product Detail -->
        <div class="b-grey-70 lg:b-b lg:pb-4" :class="mode === 'modal' ? 'lg:mb-4' : 'md:b-b md:mb-2 md:pb-4'">
          <div :class="{ 'mt-6 <lg:mb-6': mode === 'modal' && $slots.default }">
            <badge-template v-if="$feature.showBadgeAboveTitle" />
            <eyebrow-template v-else />
            <div :class="brandClasses.namePriceWrapper">
              <div v-if="$feature.showSkeletonsOnQuickshop && pending" class="mb-3 mt-5 h-3 w-20 skeleton" />
              <p
                v-else-if="details?.name"
                class="line-clamp-3"
                :class="[
                  mode === 'inline' ? brandClasses.productNameInline : brandClasses.productNameModal,
                  classProductName,
                ]"
              >
                {{ details.name }}
              </p>
              <eyebrow-template v-if="$feature.showBadgeAboveTitle" />
              <badge-template v-else />
              <div
                v-if="$feature.showSkeletonsOnQuickshop && pending && details?.price"
                class="mb-2 mt-3 h-2 w-18 skeleton"
              />
              <product-pricing
                v-else-if="!pending && !isDigitalProduct"
                class="text-sm"
                :class="mode === 'modal' ? 'mt-4' : 'mt-2'"
                :currency="(details?.currency || product?.currency)!"
                :price="productPrice"
                :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
                :show-original-price-for-omnibus="$feature.showOmnibusBadgeOnQuickshopAndPDP"
              />
            </div>
          </div>
          <slot name="belowPrice" :product="details" :product-price="productPrice" />
        </div>
      </define-product-heading>

      <base-button
        v-if="mode === 'inline'"
        :aria-label="$t.close"
        class="absolute right-0 top-0 mr-2 mt-1 <lg:hidden"
        @click="emit('resolve')"
      >
        <vf-icon name="close" size="md" />
      </base-button>

      <div v-if="$slots.default">
        <slot />
        <product-heading />
      </div>

      <div class="relative row-span-3 row-start-1 mb-2 w-full !mb-3">
        <slot name="badge">
          <product-badge
            v-if="hasBadge && (showBadgeAboveGallery || mode === 'inline')"
            v-bind="details?.badge"
            :class="[
              { 'pointer-events-none absolute right-0 z-1': !showBadgeAboveGallery },
              badgePosition === 'bottom' ? gallery.pagination ? 'bottom-4' : 'bottom-0' : 'top-0',
              brandClasses.badge,
            ]"
          />
        </slot>

        <slot name="gallery">
          <!-- Image Carousel -->
          <div
            v-if="$feature.showSkeletonsOnQuickshop && pending"
            class="mb-4 skeleton"
            :style="{ 'aspect-ratio': galleryAspectRatio }"
          />
          <vf-carousel
            v-else-if="details?.gallery?.length"
            :key="details.gallery.length"
            :class="brandClasses.carousel"
            :class-container="brandClasses.carouselContainer"
            disable-focus
            :loop="details.gallery.length > 1"
            @paginate="handleChange"
            @scroll-end="handleChange"
            @to-next="handleChange"
            @to-prev="handleChange"
          >
            <template #default="{ activeItem }">
              <div
                v-for="(item, i) in details.gallery"
                :key="i"
                class="relative w-full"
                :class="brandClasses.carouselItem"
                :style="{ 'aspect-ratio': galleryAspectRatio }"
              >
                <base-picture
                  v-if="item.type === 'image'"
                  :alt="item.alt || `${details.name} - ${i + 1}`"
                  class="absolute-0 full"
                  :height="item.height || gallery.presets.src.height"
                  :props-img="{ fetchpriority: i === 0 ? 'high' : undefined }"
                  :src="getImageTransformations(item.src, gallery.presets.src)"
                  :width="item.width || gallery.presets.src.width"
                />
                <vf-video
                  v-else-if="item.type === 'video'"
                  ref="videos"
                  v-bind="item"
                  class="absolute-0 full"
                  :inert="i !== activeItem"
                  @play="handlePlay(i)"
                />
              </div>
            </template>
            <template v-if="gallery.pagination" #pagination="{ activeItem, pages }">
              <div
                class="max-w-full flex justify-center wrap space-x-2"
                :class="[
                  mode === 'modal' ? 'mt-4' : 'mt-2',
                  { 'md:pb-5': mode === 'modal' && $slots.default },
                ]"
              >
                <div
                  v-for="(_, i) in pages"
                  :key="i"
                  :aria-current="(i === activeItem) || undefined"
                  class="h-2 w-2 b b-grey-40 rounded-full"
                  :class="{ 'bg-grey-10 b-grey-10 !b-black': i === activeItem }"
                />
              </div>
            </template>
          </vf-carousel>
        </slot>

        <slot name="belowGallery" />
      </div>

      <div class="col-start-2 px-4">
        <product-heading v-if="!$slots.default" />

        <template v-if="pending && $feature.showSkeletonsOnQuickshop">
          <!-- Color Selection Skeleton -->
          <template v-for="({ options }, j) in colorAttributes" :key="j">
            <div class="mb-3 mt-5 h-3 w-11 skeleton" />
            <div class="mb-4 mt-3 flex wrap">
              <div v-for="i in options.length" :key="i" class="h-10 w-10 p-2">
                <div class="full skeleton rounded-full" :class="{ '<lg:w-8 <lg:h-8 <lg:mx-4': mode === 'modal' }" />
              </div>
            </div>
          </template>

          <!-- Size Selection Skeleton -->
          <div class="mb-6">
            <div class="mb-3 mt-5 h-3 w-11 skeleton" />
            <div class="mt-2 flex gap-2 wrap">
              <div v-for="_size in 12" :key="_size" class="h-10 w-10 skeleton" />
            </div>
            <div class="mb-3 mt-5 h-3 w-11 skeleton" />
            <div class="h-10 max-w-full w-82 skeleton" />
          </div>
        </template>
        <div v-else-if="details && isDigitalProduct" ref="sizePickersEl" class="mb-6">
          <form-gift-card
            ref="formRef"
            v-model:attribute-selection="attributeSelection"
            v-model:form="giftCardFormFields"
            :attribute-selection-labels="attributeSelectionLabels"
            :error
            :hide-checkbox="!!context"
            hide-submit
            :pending="detailsPending"
            :product="details"
            @select-card-amount="selectSize"
            @select-card-style="(id, _, label) => selectColor(id, label)"
          />
        </div>
        <template v-else>
          <!-- Color Selection -->
          <div
            class="b-grey-70"
            :class="mode === 'modal' && $slots.default ? '<lg:b-t <lg:pt-4' : '<lg:mt-4 <lg:b-t <lg:pt-4'"
          >
            <fieldset v-for="({ label, options, type }, j) in colorAttributes" :key="j" :aria-labelledby="label">
              <p class="text-sm" :class="{ 'mt-2': mode === 'inline' }">
                <span :id="label" :class="brandClasses.label">
                  {{ label }}<span aria-hidden="true">:</span>
                </span>
                {{ attributeSelectionLabels[type] }}
              </p>
              <div
                class="flex pt-2 wrap !pt-1"
                :class="{ 'gap-1': shape === 'rectangle', 'pt-3': mode === 'inline' }"
              >
                <vf-color-picker
                  v-for="{ available, id: optionId, label: optionLabel, swatch, value } in options"
                  :key="value"
                  :active="!pending && getIsActive(value, optionId)"
                  :color="swatch?.color"
                  :name="optionLabel"
                  :thumbnail="swatch?.image"
                  :unavailable="!available"
                  :variant="shape"
                  @click="selectColor(optionId, value)"
                />
              </div>
            </fieldset>
          </div>

          <div v-if="product && isComingSoon" class="mb-6 mt-4 space-y-4">
            <p class="title-4 c-grey-20">
              {{ comingSoonText }}
            </p>
            <p v-if="selectedVariant?.notifyMe">
              {{ replaceAll($t.comingSoonDescription, { productName: product.name }) }}
            </p>
          </div>

          <!-- Size Selection -->
          <div
            v-else
            ref="sizePickersEl"
            class="mt-4 f-col gap-y-4"
            :class="mode === 'modal' && $slots.default ? 'mb-4' : 'mb-6'"
          >
            <base-form
              ref="formAttributesRef"
              class="space-y-4 space-y-6"
              :form="attributeSelection"
              :scroll-to-error="false"
            >
              <template v-for="({ label, options, type }, i) of sizeAttributes" :key="i">
                <div v-if="!options.some(({ productUrl }) => productUrl)">
                  <base-form-field v-slot="{ attrs, invalid }" :name="type" :rule="validateRequired()">
                    <fieldset :aria-labelledby="label">
                      <span class="pt-2 text-sm">
                        <span :class="brandClasses.label">
                          <span :id="label">{{ label }}</span>:
                        </span>
                        {{ attributeSelectionLabels[type] }}
                        <span v-if="type !== 'color' && showStockStatusLabel">
                          - <span class="c-red-30">{{ selectedVariant?.stockStatusLabel }}</span>
                        </span>
                      </span>
                      <vf-form-error v-if="error && invalid" class="mt-1">
                        {{ $t.selectSizeError }}
                      </vf-form-error>
                      <div
                        class="mt-2 flex gap-2 wrap pt-1"
                        :class="{ 'b-b b-grey-70 pb-6': mode === 'modal' && $slots.default }"
                      >
                        <vf-size-picker
                          v-for="({ available, label: optionLabel, value }, j) in options"
                          :key="optionLabel"
                          :class="{ 'pointer-events-none':
                            !allowOutOfStockSelection
                            && !getAttrOptionAvailable(type, value)
                            && !getAttrOptionNotifyMe(type, value),
                          }"
                          :model-value="attributeSelection[type]"
                          :name="`${i}-${details?.id || product?.id}-quickshop`"
                          :required="j === 0"
                          :type
                          :unavailable="!available || !getAttrOptionAvailable(type, value)"
                          :value
                          v-bind="attrs"
                          @input="selectSize(type, value, optionLabel)"
                        >
                          {{ optionLabel }}
                        </vf-size-picker>
                      </div>
                    </fieldset>
                  </base-form-field>
                </div>
              </template>
            </base-form>
          </div>
          <vf-notification
            v-if="mode === 'inline' && cart.notification.message"
            class="mb-6"
            :type="cart.notification.type"
          >
            {{ cart.notification.message }}
          </vf-notification>
        </template>
        <!-- Quickshop Actions -->
        <base-sticky name="footerQuickshop" position="bottom">
          <div
            class="bg-white"
            :class="[
              brandClasses.stickyFooter,
              {
                '<lg:b-t <lg:b-grey-70': !$slots.viewDetailsCta,
                '<lg:sticky <lg:bottom-0 <lg:pb-3 <lg:pt-2': !$slots.default,
                'bottom-0': mode === 'modal' && !$slots.default,
              },
            ]"
          >
            <vf-button
              v-if="showNotifyCta"
              class="w-full "
              :size="ctaSize"
              @click="viewDetails"
            >
              {{ $t.notifyCta }}
            </vf-button>
            <vf-button
              v-else-if="isComingSoon && !pending"
              class="w-full "
              disabled
              :size="ctaSize"
            >
              {{ comingSoonLabel }}
            </vf-button>
            <vf-button
              v-else-if="(isSelectedVariantOutOfStock || allVariantsOutOfStock) && !pending"
              class="w-full "
              disabled
              :size="ctaSize"
            >
              {{ $t.outOfStock }}
            </vf-button>
            <vf-button
              v-else-if="isSignInToBuy"
              class="w-full "
              :class="{ 'c-white': theme }"
              :size="ctaSize"
              :theme
              :variant="theme ? 'themable' : 'primary'"
              @click="signInToBuy"
            >
              {{ isNonLoyaltyMember ? $t.enrollInLoyaltyToBuy : $t.signInToBuy }}
            </vf-button>
            <div v-else-if="context" class="min-w-1/2 w-full grow">
              <vf-button class="w-full " :loading :size="ctaSize" @click="updateInCart">
                {{ $t.updateInCart }}
              </vf-button>
            </div>
            <div v-else class="grow">
              <vf-button
                class="w-full "
                data-test-id="quickshop-add-to-cart"
                :loading="loading || pending"
                :size="ctaSize"
                @click="addToCart"
              >
                {{ $t.addToCart }}
              </vf-button>
            </div>
            <slot :attribute-selection="attributeSelection" name="viewDetailsCta" :product="details">
              <vf-button
                class="w-full "
                :loading="pending"
                :size="ctaSize"
                :variant="viewDetailsVariant"
                @click="viewDetails"
              >
                {{ $t.viewDetails }}
              </vf-button>
            </slot>
          </div>
        </base-sticky>
      </div>
    </div>
    <dialog-loyalty-sign-up />
  </vf-dialog-layout>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType, BaseVideo as BaseVideoType, FormGiftCard as FormGiftCardType } from '#components'
import type { Inventory, Product, ProductPrice, ProductVariants } from '#root/api/clients/product/data-contracts'
import type { GiftOption } from '#types/cart'
import type { CSSClass } from '#types/common'
import type { InteractionOrigin, ProductContext, ShippingMethodContext } from '#types/gtm'
import type { GiftCardFormFields } from '#types/product'
import type { LocationQueryRaw } from 'vue-router'
import { getInteractionOrigin, interactionOriginMap, setInteractionOrigin } from '#commerce/utils/gtm'
import { ApiErrorCode } from '#root/enums/api'

export type Props = {
  productId?: string
  product?: Product
  closeOnAdd?: boolean
  // flag to determine whether to show Add to Cart or Update in Cart button
  context?: 'update-cart-item' | 'update-saved-item'
  mode?: 'modal' | 'inline'
  giftOption?: GiftOption
  classProductName?: CSSClass
  classDialogWrapper?: CSSClass
  initialAttributes?: ProductVariants[number]['attributes']
  updateProductId?: (id: string) => void
  categoryURL?: string
  gtmConfig?: ProductContext & ShippingMethodContext & { appSource?: InteractionOrigin }
  storeId?: string
  title?: string
}
export type Emits = {
  resolve: []
}

const {
  categoryURL,
  classDialogWrapper = '!p-0',
  closeOnAdd,
  context,
  giftOption,
  gtmConfig,
  initialAttributes,
  mode = 'modal',
  product,
  storeId,
} = defineProps<Props>()

const emit = defineEmits<Emits>()

const productId = defineModel<string>('productId', { required: true })

const currentDialog = useCurrentDialog<Props, Emits>()
const { DialogSignIn, DialogLoyaltySignUp } = useDialogsStore()
const [DefineProductHeading, ProductHeading] = createReusableTemplate()
const [DefineBadgeTemplate, BadgeTemplate] = createReusableTemplate()
const [DefineEyebrowTemplate, EyebrowTemplate] = createReusableTemplate()

// Global
const config = useAppConfig()
const {
  brandClasses,
  brandStyles,
  ctaSize,
  gallery,
  titleTranslationKey,
  viewDetailsVariant
} = config.components.dialog.quickshop
const {
  badgePosition,
  maxCarouselItems,
  showBadgeAboveGallery,
  signInToBuyCta: { theme }
} = config.components.product.card
const { shape } = config.components.product.swatches || {}
const { allowOutOfStockSelection, enableColorAsAttribute, scrollToNotifyMe } = config.pages.pdp
const { name } = useAttrs()
const auth = useAuthStore()
const user = useUserStore()
const cart = useCartStore()
const { validateRequired } = useLocalizedValidators()
const { $feature, $gtm, $t, $viewport, $sendExtraMonetateEvents } = useNuxtApp()
const route = useRoute()
const router = useRouter()
const toast = useToaster()
const gtmTrace = useSessionStorage('gtmTrace', {} as Record<string, ShippingMethodContext>)
const { trackEvent } = useOptimizely()

// Local
const galleryAspectRatio = gallery.presets.src.width / gallery.presets.src.height
const attributeSelection = reactive(initialAttributes || {}) as unknown as ProductVariants[number]['attributes']
const activeProduct = computed(() => productId.value || product?.id || '')
const error = ref(false)
const filtersQuery = JSON.parse(route.query.filters as string || '{}')
const loading = ref(false)
const sizePickersEl = ref<HTMLElement>()
const parentInteractionOrigin = ref(getInteractionOrigin())
const videos = ref<typeof BaseVideoType[]>([])
const formRef = ref<InstanceType<typeof FormGiftCardType>>()
const formAttributesRef = ref<InstanceType<typeof BaseFormType> | null>(null)

const giftCardFormFields = ref<GiftCardFormFields>({
  email: giftOption?.email || '',
  fullName: giftOption?.to || '',
  message: giftOption?.message || '',
  amount: (giftOption && initialAttributes?.size === 'X' && product?.price.lowCurrent.toString()) || '',
  size: attributeSelection?.size || ''
})

// Fetch
const { productPending: detailsPending, product: details } = await useProduct(activeProduct, {
  server: false,
  // @ts-expect-error incorrect Nuxt types
  default: () => (product),
  // Temporary fix for missing data from API
  transform: (input) => ({
    ...input,
    eyebrow: input.eyebrow || product?.eyebrow,
    badge: input.badge || product?.badge,
    ...(maxCarouselItems && { gallery: input.gallery?.slice(0, maxCarouselItems) || [] })
  }),
  key: `quickshop-${activeProduct.value}`,
  onResponse: async ({ response: { _data } }) => productId.value = _data.id,
  onResponseError: ({ response: { _data } }) => {
    console.error(_data)
    emit('resolve')
  }
})

const { getAttrOptionAvailable, getAttrOptionNotifyMe } = getProductAvailability(attributeSelection, details)
const url = computed(() => details.value?.url || product?.url)
const hasBadge = computed(() => details.value?.badge?.label || details.value?.badge?.icon)
const isDigitalProduct = computed(() => details.value?.productType?.toLowerCase() === 'digital')
const isComingSoon = computed(() => $feature.showComingSoonOnQuickshop && getIsComingSoonProduct(details.value))
const comingSoonLabel = computed(() =>
  isComingSoon.value ? (details.value?.badge || details.value?.eyebrow)?.label : undefined)

const isNonLoyaltyMember = computed(() => !!(auth.loggedIn && !user.isLoyaltyMember))
const isSignInToBuy = computed(() =>
  !!details.value?.presale
  && (!auth.loggedIn || isNonLoyaltyMember.value))
const eyebrowStyle = computed(() => {
  const colorHexCode = details?.value?.eyebrow?.colorHexCode
  return colorHexCode && colorHexCode !== 'None' ? { style: `color: #${colorHexCode}` } : {}
})

let inventory = ref<Inventory | null>(null)
let inventoryPending = ref(false)

// We want to skip the inventory check for digital products, but we only know in advance that
// the product is digital once cart/saved item updated - on PLP/SRP we can only tell after we've
// fetched the product details, which would mean having to wait before calling the inventory.
// Doing an extra request is a better proposition than having synchronous requests,
// so we'll also call the inventory service for digital products, and just won't use the response.
if (!giftOption) {
  ({ pending: inventoryPending, data: inventory } = useApi().products.inventory(activeProduct, {}, {
    key: `productInventory-${activeProduct.value}`
  }))
}

const pending = computed(() => detailsPending?.value || inventoryPending?.value)

const variants = computed(() => Array.isArray(details.value?.variants)
  ? details.value.variants.map((variant) => {
      const { quantity = 0 } = inventory.value?.variants[variant.id] || {}
      return {
        ...variant,
        productInventoryState: isDigitalProduct.value || quantity > 0 ? 'InStock' : 'OutOfStock'
      }
    })
  : [])

// Attribute selection from filtersQuery
const initialFilterSelection = computed(() => {
  const initialSelection = {}
  Object.keys(filtersQuery).forEach((label) => {
    // Find attribute by label, URL filters use label instead of type
    const attribute = details.value?.attributes.find((attribute) => {
      const attributeLabel = attribute.label?.toLowerCase()
      const filterLabel = label?.toLowerCase()

      return filterLabel?.includes(attributeLabel) || attributeLabel?.includes(filterLabel!)
    })

    if (attribute) {
      const option = attribute?.options.find((option) => {
        // Select if there is only one filter option
        const [filterOption] = filtersQuery?.[label]
        return option.label?.toLowerCase() === filterOption?.toLowerCase() && filtersQuery[label].length === 1
      })
      // Set initial selection for an attribute type
      if (option)
        initialSelection[attribute.type] = option.value
    }
  })
  return initialSelection
})

const attributes = computed(() => details.value?.attributes.map((attribute) => ({
  ...attribute,
  options: attribute.options.map((option) => {
    // VariantIds are a list of all variant IDs
    const variantIds = variants.value
      .filter((variant) => variant.attributes[attribute.type] === option.value)
      .map((variant) => variant.id)
    return attribute.type === 'color'
      ? {
          ...option,
          available: (option.id === activeProduct.value)
            ? variants.value.some(({ productInventoryState }) => productInventoryState === 'InStock')
            : option.available
        }
      : {
          ...option,
          available: variants.value.filter(({ id }) => variantIds?.includes(id))
            .some(({ productInventoryState }) => productInventoryState === 'InStock')
        }
  })
})) || [])

const attributeSelectionLabels = computed(() =>
  attributes.value?.reduce((attributes, { type, options }) => {
    attributes[type] = options?.find((option) => option.value === attributeSelection[type])?.label
    return attributes
  }, {})
)

const colorAttributes = computed(() => attributes.value.filter(({ type }) => type === 'color'))
const sizeAttributes = computed(() => attributes.value.filter(({ type }) => type !== 'color'))
const isVariantPicked = computed(() => attributes.value.every(({ type }) =>
  attributeSelection[type]
  || type === 'color'
  || (type === 'size' && giftCardFormFields.value.amount))
)
const selectedVariant = computed(() => {
  const variant = variants.value.find((variant) =>
    Object.entries(variant.attributes)?.every(
      ([key, val]) =>
        attributeSelection[key] === val
        || (key === 'size' && val === 'X' && giftCardFormFields.value.amount)
    )
  )
  return variant && isVariantPicked.value ? variant : null
})
const isSelectedVariantOutOfStock = computed(() => isVariantPicked.value && selectedVariant.value?.productInventoryState === 'OutOfStock')
const allVariantsOutOfStock = computed(() => variants.value.every((variant) => variant.productInventoryState === 'OutOfStock'))
const allVariantsNotifyMe = computed(() => variants.value.every(({ notifyMe }) => notifyMe))

const showNotifyCta = computed(() =>
  ((isComingSoon.value && allVariantsNotifyMe.value)
    || ($feature.showNotifyMeOnQuickshop && selectedVariant.value?.notifyMe && isSelectedVariantOutOfStock.value)
    || (selectedVariant.value?.notifyMe && isComingSoon.value))
  && !pending.value)

const productPrice = computed<ProductPrice>(() => {
  if (selectedVariant.value?.price) {
    const { current, original, historicBestPrice, percentagePriceDiscount } = selectedVariant.value.price
    return {
      highCurrent: current ?? original,
      lowCurrent: current ?? original,
      highOriginal: original,
      lowOriginal: original,
      historicBestPrice,
      percentagePriceDiscount
    }
  }
  return details.value?.price || product?.price || { highCurrent: 0, lowCurrent: 0, highOriginal: 0, lowOriginal: 0 }
})

const itemGiftOption = computed(() => isDigitalProduct.value
  ? {
      to: giftCardFormFields.value.fullName,
      email: giftCardFormFields.value.email,
      message: giftCardFormFields.value.message
    }
  : undefined
)
const itemCustomAmount = computed(() => giftCardFormFields.value.amount
  ? {
      amount: +giftCardFormFields.value.amount
    }
  : undefined
)

const comingSoonText = computed(() => {
  const targetPublishDate = product?.targetPublishDate ?? details.value?.targetPublishDate

  return targetPublishDate
    ? `${$t.comingSoon} - ${formatTargetPublishDate(targetPublishDate, $feature.configProductComingSoonDateFormat)}`
    : $t.comingSoon
})

async function addToCart() {
  error.value = (formRef.value && !formRef.value?.validate(true)) || !isVariantPicked.value

  if (error.value || !selectedVariant.value) {
    sizePickersEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    formAttributesRef.value?.validate(true)
    return
  }

  loading.value = true

  if (selectedVariant.value.id) {
    setGtmCartProductsMap(selectedVariant.value.id, {
      ...details.value,
      category: gtmConfig?.category,
      list: gtmConfig?.list
    })
  }

  try {
    const { shippingMethod, shippingStoreId, shippingWindow } = gtmConfig || {}

    if (shippingMethod && shippingStoreId && shippingWindow)
      gtmTrace.value[selectedVariant.value!.id] = { shippingMethod, shippingStoreId, shippingWindow }

    await withRetryAfterTime(() => cart.add({
      product: selectedVariant.value!.id,
      upc: selectedVariant.value!.upc,
      suppressMiniCart: ['shopTheLook', 'minicart'].includes(name as string),
      giftOption: itemGiftOption.value,
      customAmount: itemCustomAmount.value,
      maxQty: inventory.value?.variants[selectedVariant.value!.id]?.quantity,
      extendedGtmConfig: {
        ...gtmConfig,
        category: gtmConfig?.category || interactionOriginMap[gtmConfig?.appSource!]
      },
      storeId: shippingStoreId
    }), 2000, [ApiErrorCode.GWP_PRODUCT])
    $sendExtraMonetateEvents()
    trackEvent('quick_shop', {
      product: details.value?.id || activeProduct.value
    })
    emit('resolve')
  }
  catch (err) {
    log.error(err)
    delete gtmTrace.value[selectedVariant.value!.id]
  }

  loading.value = false
  resetInteractionOrigin()

  if (cart.error) {
    cart.setNotification('error', cart.errorMessage)
    if (selectedVariant.value.id && details.value) removeGtmCartProductsMap(selectedVariant.value.id, details.value.id)
  }
  else {
    formRef.value?.reset()
  }

  if (!$feature.showMiniCartOnAddProduct || closeOnAdd)
    emit('resolve')
}

const updateInCart = async () => {
  loading.value = true
  error.value = (formRef.value && !formRef.value?.validate(true)) || !isVariantPicked.value
  try {
    if (error.value || !selectedVariant.value || !product?.itemId) {
      sizePickersEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
      formAttributesRef.value?.validate(true)
      return
    }

    if (context === 'update-cart-item') {
      const { itemId = '' } = product || {}
      const currentItem = itemId && cart.items.find(({ id }) => id === itemId)

      if (selectedVariant.value.id)
        setGtmCartProductsMap(selectedVariant.value.id, { ...details.value, list: gtmConfig?.list })

      if (currentItem)
        setGtmCartProductVariant(selectedVariant.value.id, getGTMProduct(currentItem.productId))

      await cart.updateItemInCart(
        product!.itemId as string,
        product!.id as string,
        activeProduct.value,
        selectedVariant.value!.id,
        selectedVariant.value!.upc,
        itemGiftOption.value,
        itemCustomAmount.value,
        storeId
      )
    }

    if (context === 'update-saved-item')
      await cart.updateSavedItemInCart(product!.itemId as string, selectedVariant.value!.id as string, url.value)

    await toast.add({
      props: {
        title: $t.success,
        message: replaceAll($t.cartItemEdited, { name: details.value?.name }),
        type: 'success',
      },
      promise: true
    })

    emit('resolve')
  }
  catch (err) {
    assertApiError(err)
    error.value = true

    switch (err.errorId) {
      case ApiErrorCode.ADDED_MAX_QUANTITY:
        toast.add({
          props: {
            type: 'error',
            message: cart.getMaxAllowedQtyMessage(err)
          }
        })
        emit('resolve')
        break
      case ApiErrorCode.ITEM_OOS:
      case ApiErrorCode.ITEMS_OOS:
        cart.setNotification('error', err.message || $t.unableToUpdateCartItem, 'section')
        break
    }
  }
  finally {
    loading.value = false
  }
}

const signInToBuy = async () => {
  if (isNonLoyaltyMember.value) {
    await DialogLoyaltySignUp
      .keep($viewport.lg)
      .open({ formLocation: 'modal:single:myaccount:none', isSignInToBuy: true })
  }
  else {
    await DialogSignIn
      .keep($viewport.lg)
      .open({ formLocation: 'modal:single:pdp:none', isSignInToBuy: true })
  }

  if (auth.loggedIn && !isNonLoyaltyMember.value) {
    if (mode !== 'inline' && context !== 'update-cart-item') return currentDialog.reopen({ initialAttributes: attributeSelection })
    await until(() => cart.pending).toBe(false)
    context === 'update-cart-item' ? updateInCart() : addToCart()
  }
}

const handleChange = ({ activeItem }) => {
  videos.value.forEach((video) => video.pause())
  const currentVideo = details.value?.gallery?.[activeItem]?.type === 'video'
    ? details.value?.gallery?.[activeItem]
    : null
  if (currentVideo) {
    const currentVideoRef = videos.value.find(
      ({ videoRef }) => getValueForBreakpoint($viewport.breakpoint, videoRef.src) === currentVideo.src
    )
    currentVideoRef?.play()
  }
}

const handlePlay = (i: number) => videos.value[i].play()

const preselectAttributes = () => {
  attributes.value.forEach(({ type, options }) => {
    const availableOptions = options.filter(({ available }) => available)
    if (type === 'color') {
      const colorSelection = colorAttributes.value[0].options.find(({ id }) => id === activeProduct.value)?.value
      const defaultColor = (options?.find(({ defaultColor }) => defaultColor) || options[0])?.value

      attributeSelection.color = colorSelection || defaultColor
    }
    else if (options.length === 1) {
      attributeSelection[type] = options[0].value
    }
    else if (availableOptions.length === 1) {
      attributeSelection[type] = availableOptions[0].value
    }
    else if (type === 'fitType') {
      attributeSelection[type] = options?.[0]?.value || options?.[0]?.label
    }
    else {
      attributeSelection[type] = initialAttributes?.[type] || initialFilterSelection.value[type] || attributeSelection[type] || ''
    }

    if (!attributeSelection[type] && $feature.enablePreselectFirstWidth && type === 'width')
      attributeSelection[type] = availableOptions[0]?.value || options[0].value
  })
  formAttributesRef.value?.validate()
}

const getIsActive = (value: string, id: string) =>
  enableColorAsAttribute ? attributeSelection.color === value : activeProduct.value === id

const selectColor = (id: string, label: string) => {
  attributeSelection.color = label
  productId.value = id
  $gtm.push('plpPage.onSelectColor', label)
}

const selectSize = (type: string, value: string, label: string) => {
  attributeSelection[type] = value
  giftCardFormFields[type] = value

  const eventLabel = `${activeProduct.value} - ${label}`

  if (name === 'shopTheLook')
    $gtm.push('shopTheLook.onSelectSize', eventLabel, { bundleId: gtmConfig!.bundleId! })
  else
    $gtm.push('plpPage.onSelectSize', eventLabel)
}

const viewDetails = async () => {
  $gtm.push('product.onProductClick', (details?.value || product)!, {
    action: 'Click View Details',
    breadcrumbs: deserializeAnalyticsBreadcrumbs(history.state.breadcrumbs),
    category: history.state.category,
    searchTerm: route.query.q?.toString()
  })

  emit('resolve')

  const selection = Object.entries(attributeSelection).filter(([key, value]) =>
    enableColorAsAttribute ? value : key !== 'color' && value
  )

  await router.push({
    path: url.value,
    state: {
      breadcrumbs: history.state.breadcrumbs,
      category: history.state.category,
      searchTerm: route.query.q?.toString(),
      categoryURL
    },
    hash: scrollToNotifyMe && isMobile() ? '#notify-me' : undefined,
    query: {
      ...(selection.length && {
        ...Object.fromEntries(selection)
      })
    } as unknown as LocationQueryRaw
  })
}

const pushProductDetailViewEvent = (product) => {
  $gtm.push('product.onProductDetailView', product, {
    viewType: 'Quick Shop',
    breadcrumbs: deserializeAnalyticsBreadcrumbs(history.state.breadcrumbs),
    bundleId: gtmConfig?.bundleId,
    category: interactionOriginMap[gtmConfig?.appSource!] || gtmConfig?.category || history.state.category,
    searchTerm: route.query.q?.toString(),
    stockStatus: inventory.value?.variants[product?.masterId!]?.stockStatus,
    list: gtmConfig?.list
  })
}

const showStockStatusLabel = computed<boolean>(() =>
  $feature.showSizeLevelStockMessagingOnPDP && !!selectedVariant.value?.stockStatusLabel)

// Push updated event with inventory data once it becomes available
watch(inventory, (newInventory) => {
  if (newInventory && details.value)
    pushProductDetailViewEvent(details.value)
}, { immediate: false })

onMounted(async () => {
  if (currentDialog.reopened) {
    await until(sizePickersEl).toBeTruthy()
    addToCart()
  }
  cart.clearNotification()

  if (interactionOriginMap[gtmConfig?.appSource!])
    setInteractionOrigin(gtmConfig!.appSource, true)
  else
    setInteractionOrigin()
})

onUnmounted(() => {
  if (!closeOnAdd)
    cart.clearNotification()

  if (interactionOriginMap[gtmConfig?.appSource!])
    setInteractionOrigin(parentInteractionOrigin.value || '')
})

if (!closeOnAdd) {
  watch(cart.notification, () => {
    if (cart.notification.message)
      scrollToElement('notificationSection')
  })
}

whenever(() => !pending.value, () => {
  preselectAttributes()
})
</script>
